import type { Catalog } from '../types';

// Identity steps: EmailStep, LoginStep, AccountConversion, ExpeditedCheckoutStep,
// LoginFlow, GoogleSignInButton. Server-delivered strings (product names, API
// error messages) are NOT here — they render verbatim or interpolate as {vars}.
export const identity: Catalog = {
  // Shared email-field chrome (EmailStep's guest view and LoginStep).
  'emailField.label': 'Email address',
  'emailField.placeholder': 'you@example.com',
  'emailField.invalid': 'Enter a valid email address',
  // Fragment: the suggested address renders after it inside a <button>,
  // followed by a literal "?".
  'emailField.didYouMean': 'Did you mean',

  // EmailStep: the identity gate (choices, mounted ezAuth form, guest email
  // form, and the post-auth completing transition).
  'emailStep.eyebrow.account': 'Account',
  'emailStep.eyebrow.contact': 'Contact',
  'emailStep.title.signIn': 'Sign in to continue',
  'emailStep.subtitle.named': 'Sign in or continue as a guest to buy {purchaseName}.',
  'emailStep.subtitle.generic': 'Sign in or continue as a guest to complete your purchase.',
  'emailStep.subtitle.donation': 'Sign in or continue as a guest to make your donation.',
  // Same three, for a domain with guest checkout off (no guest button renders).
  'emailStep.subtitle.signInOnly.named': 'Sign in to buy {purchaseName}.',
  'emailStep.subtitle.signInOnly.generic': 'Sign in to complete your purchase.',
  'emailStep.subtitle.signInOnly.donation': 'Sign in to make your donation.',
  'emailStep.login': 'Log in',
  'emailStep.continueAsGuest': 'Continue as guest',
  'emailStep.createAccount': 'Create account',
  'emailStep.or': 'or',
  'emailStep.title.login': 'Log in',
  'emailStep.title.register': 'Create your account',
  // Passwordless-first standalone login (intent="login"): the magic-link email
  // form is the primary action — a returning guest/Google visitor has no
  // password — and the ezAuth password form is opt-in behind havePassword.
  'emailStep.title.access': 'Access your purchase',
  'emailStep.subtitle.access': "Enter the email you used at checkout and we'll send you a sign-in link.",
  'emailStep.havePassword': 'Have a password? Sign in',
  // Our title over ezAuth's forgot screen (its own heading is hidden).
  'emailStep.title.reset': 'Reset password',
  // Forgot screen: escape back to the sign-in form; the login label renders as
  // an inline link after this fragment.
  'emailStep.rememberPassword': 'Remember your password?',
  // The checkout-gate auth view's shell label (a standalone login uses the
  // access title instead).
  'emailStep.aria.signInForm': 'Sign in',
  'emailStep.magicLink': 'Email me a sign-in link',
  'emailStep.aria.signingIn': 'Signing in',
  'emailStep.signingIn': 'Signing you in…',
  'emailStep.settingUpCheckout': 'Setting up your checkout.',
  'emailStep.guest.title': 'Enter your email',
  'emailStep.guest.subtitle': 'We will use this email for your receipt and account access.',
  'emailStep.guestCta.payment': 'Continue to payment',
  'emailStep.guestCta.paymentLoading': 'Starting checkout…',
  'emailStep.guestCta.trial': 'Continue to start trial',
  'emailStep.guestCta.trialLoading': 'Starting trial…',
  // Deliverability gate (verifyEmail): shown inline when the backend can't
  // confirm the guest address receives email. The CTA switches to
  // continueAnyway for the second submit of the same address.
  'emailStep.verifyWarning': 'This address appears to be invalid — please confirm your email is correct.',
  'emailStep.continueAnyway': 'Continue anyway',

  // LoginStep: the magic-link request form (mid-checkout and standalone). Its
  // title/subtitle/button reuse the emailStep access keys — the email-for-link
  // form reads identically wherever it appears.
  'loginStep.sent.title': 'Check your email',
  'loginStep.sent.body': 'If this email has access, we sent a login link. Open it on this device and we will bring you back to this page.',
  // Sent-screen recovery fallbacks. No contact-support key: the shell renders
  // its own publisher support line when the publisher configured one.
  'loginStep.sent.help': "Didn't get the email?",
  'loginStep.sent.spam': 'Check your spam folder.',
  'loginStep.sent.differentEmail': 'Use a different email',
  'loginStep.submitting': 'Sending link...',

  // AccountConversion: the post-purchase "create an account" offer.
  // Fragments: the guest email renders between them wrapped in <strong>.
  'accountConversion.promptBefore': 'Create an account for',
  'accountConversion.promptAfter': 'to sign in faster next time — no waiting for an email link.',
  'accountConversion.createAccount': 'Create account',
  'accountConversion.noThanks': 'No thanks, continue',
  // Fragment: renders inside <strong>, followed by createdDetail.
  'accountConversion.created': 'Account created.',
  'accountConversion.createdDetail': 'Next time you can sign in instead of waiting for a link.',
  // The password-only register form (the email is sourced from the attestation,
  // never shown as an editable field). Labels follow the widget's .sm-field
  // pattern (label above the input, like EmailStep).
  'accountConversion.passwordLabel': 'Password',
  'accountConversion.confirmLabel': 'Confirm password',
  'accountConversion.creating': 'Creating account...',
  'accountConversion.passwordTooShort': 'Password must be at least 8 characters.',
  'accountConversion.passwordMismatch': 'Passwords do not match.',
  'accountConversion.passwordCompromised': 'That password has appeared in a data breach. Please choose another.',
  'accountConversion.accountExists': 'An account already exists for this email. Log in instead.',
  'accountConversion.failed': 'Could not create the account. Please try again.',

  // ExpeditedCheckoutStep: the single-screen guest checkout.
  'expeditedCheckout.title': 'Checkout',
  'expeditedCheckout.title.trial': 'Start your free trial',
  'expeditedCheckout.subtitle': 'Enter payment details below.',
  'expeditedCheckout.haveAccount': 'Have an account?',
  'expeditedCheckout.login': 'Log in',
  // Rejection reason for a confirm attempted after the mount-time session
  // expired; TrialPaymentForm renders the rejecting error's message inline.
  'expeditedCheckout.sessionInvalid': 'The checkout session is no longer valid.',

  // LoginFlow: fallback when a magic-link request fails without a server message.
  'loginFlow.sendLinkError': 'Unable to send login link',

  // GoogleSignInButton: the GIS/ezAuth exchange failed after Google signed in.
  'googleSignIn.error': 'We could not complete Google sign-in. Please try again.',

  // SocialSignInButtons: {provider} is the brand name (Facebook, Apple, Microsoft).
  'socialSignIn.continueWith': 'Continue with {provider}',
  'socialSignIn.error': 'We could not complete {provider} sign-in. Please try again.',
  'socialSignIn.popupBlocked': 'Your browser blocked the {provider} sign-in window. Allow pop-ups for this site and try again.',
  'socialSignIn.emailRequired': '{provider} did not share an email address. Allow email access or sign in another way.',
  'socialSignIn.accountExists': 'An account with this email already exists. Sign in the way you did before.',
};
